import { useEffect, useState } from 'react'
import {
  curatorApi,
  getCuratorPassword,
  setCuratorPassword,
  type Category,
  type Era,
  type GeoGranularity,
  type PhotoRow,
  type VerifyPayload,
} from '../lib/api'
import { eraForYear } from '../lib/eras'

const ERAS: { value: Era; label: string }[] = [
  { value: 'khedival',  label: 'Khedival (pre-1882)' },
  { value: 'british',   label: 'British (1882–1922)' },
  { value: 'monarchy',  label: 'Monarchy (1922–1952)' },
  { value: 'nasser',    label: 'Nasser (1952–1970)' },
  { value: 'sadat',     label: 'Sadat (1970–1981)' },
  { value: 'mubarak',   label: 'Mubarak (1981–2011)' },
  { value: 'post_2011', label: 'Post-2011' },
]

const CATEGORIES: Category[] = [
  'street', 'political', 'figure', 'cinema', 'architecture', 'military', 'sports',
]

const GRANULARITIES: GeoGranularity[] = ['building', 'neighborhood', 'city', 'governorate']

const EVENT_ANCHORS = [
  '', 'free_officers_1952_07_23', 'suez_speech_1956_07_26', 'suez_crisis_1956',
  'six_day_war_1967', 'october_war_1973_10_06', 'bread_riots_1977_01',
  'knesset_visit_1977_11_20', 'camp_david_1978_09_17', 'sadat_assassination_1981_10_06',
  'tahrir_2011_01_25', 'mubarak_resign_2011_02_11',
]

const CHECK_LABELS: Record<keyof Pick<VerifyPayload,
  'v1_provenance' | 'v2_license' | 'v3_year_primary' | 'v4_year_second_src' | 'v5_location' | 'v6_no_conflict'
>, string> = {
  v1_provenance: '1. Provenance — stable archive URL + persistent ID',
  v2_license: '2. License — PD / CC0 / CC-BY / CC-BY-SA',
  v3_year_primary: '3. Year (primary) — from archive metadata',
  v4_year_second_src: '4. Year (secondary) — ≥2 independent sources agree within ±1, OR anchored to event date',
  v5_location: '5. Location — ≥1 source confirms to neighborhood; landmark verifiable',
  v6_no_conflict: '6. No-conflict + pHash dedup — no reputable source disagrees',
}

type CheckKey = keyof typeof CHECK_LABELS

export default function Curate() {
  const [authed, setAuthed] = useState(!!getCuratorPassword())
  const [pwInput, setPwInput] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)

  const [list, setList] = useState<Partial<PhotoRow>[]>([])
  const [ingestUrl, setIngestUrl] = useState('')
  const [ingestError, setIngestError] = useState<string | null>(null)
  const [ingesting, setIngesting] = useState(false)
  const [selected, setSelected] = useState<PhotoRow | null>(null)

  useEffect(() => {
    if (!authed) return
    curatorApi.me()
      .then(() => loadList())
      .catch(() => setAuthed(false))
  }, [authed])

  function loadList() {
    curatorApi.listPhotos('candidate').then((r) => setList(r.photos)).catch(() => {})
  }

  async function tryLogin(e: React.FormEvent) {
    e.preventDefault()
    setAuthError(null)
    setCuratorPassword(pwInput)
    try {
      await curatorApi.me()
      setAuthed(true)
    } catch (err) {
      setAuthError((err as Error).message)
    }
  }

  async function doIngest(e: React.FormEvent) {
    e.preventDefault()
    setIngestError(null)
    setIngesting(true)
    try {
      const { photo } = await curatorApi.ingest(ingestUrl.trim())
      setSelected(photo)
      setIngestUrl('')
      loadList()
    } catch (err) {
      const e = err as Error & { existing?: PhotoRow }
      if (e.message === 'duplicate' && e.existing) {
        setIngestError(`Duplicate (pHash match). Existing photo: ${e.existing.id}`)
        setSelected(e.existing)
      } else {
        setIngestError(e.message)
      }
    } finally {
      setIngesting(false)
    }
  }

  if (!authed) {
    return (
      <main className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <form onSubmit={tryLogin} className="bg-white border border-stone-200 rounded-lg p-6 w-full max-w-sm space-y-4">
          <h1 className="text-xl font-bold">Egyptle curator</h1>
          <p className="text-sm text-stone-500">Enter the dev curator password.</p>
          <input
            type="password"
            autoFocus
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            className="w-full border border-stone-300 rounded px-3 py-2"
            placeholder="password"
          />
          {authError && <div className="text-sm text-red-600">{authError}</div>}
          <button type="submit" className="w-full bg-stone-900 text-white rounded py-2">
            Unlock
          </button>
          <p className="text-xs text-stone-400">Default: <code>egyptle-dev</code></p>
        </form>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-stone-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-baseline justify-between">
          <h1 className="text-2xl font-bold">Curator</h1>
          <a href="/" className="text-sm text-stone-500 hover:text-stone-700 underline">← game</a>
        </header>

        <section className="bg-white border border-stone-200 rounded-lg p-4">
          <h2 className="font-semibold mb-3">Ingest from archive URL</h2>
          <form onSubmit={doIngest} className="flex gap-2">
            <input
              type="url"
              required
              value={ingestUrl}
              onChange={(e) => setIngestUrl(e.target.value)}
              placeholder="https://commons.wikimedia.org/wiki/File:... or https://www.loc.gov/item/..."
              className="flex-1 border border-stone-300 rounded px-3 py-2 text-sm"
              disabled={ingesting}
            />
            <button
              type="submit"
              disabled={ingesting}
              className="bg-stone-900 text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            >
              {ingesting ? 'Fetching…' : 'Fetch'}
            </button>
          </form>
          {ingestError && <div className="text-sm text-red-600 mt-2">{ingestError}</div>}
        </section>

        {selected && <VerifyForm photo={selected} onSaved={(updated) => {
          setSelected(updated.status === 'verified' ? null : updated)
          loadList()
        }} onCancel={() => setSelected(null)} />}

        <section className="bg-white border border-stone-200 rounded-lg p-4">
          <h2 className="font-semibold mb-3">Candidate queue ({list.length})</h2>
          {list.length === 0 && <div className="text-sm text-stone-400">No candidates. Ingest one above.</div>}
          <ul className="divide-y divide-stone-100">
            {list.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => p.id && curatorApi.getPhoto(p.id).then(setSelected)}
                  className="w-full text-left py-2 hover:bg-stone-50 px-2 -mx-2 rounded text-sm"
                >
                  <span className="font-mono text-xs text-stone-400">{p.id?.slice(0, 8)}…</span>
                  {' '}<span className="text-stone-700">{p.place_name_en ?? '?'}</span>
                  {' '}<span className="text-stone-500">· {p.year ?? '?'}</span>
                  {' '}<span className="text-stone-400">· {p.source_archive}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  )
}

type VerifyFormProps = {
  photo: PhotoRow
  onSaved: (updated: PhotoRow) => void
  onCancel: () => void
}

function VerifyForm({ photo, onSaved, onCancel }: VerifyFormProps) {
  const [form, setForm] = useState<VerifyPayload>(() => ({
    year: photo.year || 1950,
    year_min: photo.year_min || photo.year || 1950,
    year_max: photo.year_max || photo.year || 1950,
    era: photo.era || 'monarchy',
    lat: photo.lat || 30.0444,
    lng: photo.lng || 31.2357,
    geo_granularity: photo.geo_granularity || 'city',
    place_name_en: photo.place_name_en || '',
    place_name_ar: photo.place_name_ar || '',
    place_name_historical: photo.place_name_historical || undefined,
    category: photo.category || 'street',
    subjects: photo.subjects ? JSON.parse(photo.subjects) : [],
    event_anchor: photo.event_anchor || undefined,
    caption_en: photo.caption_en || '',
    caption_ar: photo.caption_ar || '',
    trivia_en: photo.trivia_en || undefined,
    trivia_ar: photo.trivia_ar || undefined,
    license: photo.license || 'unknown',
    license_url: photo.license_url || undefined,
    attribution_text: photo.attribution_text || '',
    v1_provenance: photo.v1_provenance === 1,
    v2_license: photo.v2_license === 1,
    v3_year_primary: photo.v3_year_primary === 1,
    v4_year_second_src: photo.v4_year_second_src === 1,
    v5_location: photo.v5_location === 1,
    v6_no_conflict: photo.v6_no_conflict === 1,
    verification_notes: photo.verification_notes ? JSON.parse(photo.verification_notes) : {},
    nostalgia_score: photo.nostalgia_score || 5,
    curator: photo.curator || 'me',
  }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subjectsRaw, setSubjectsRaw] = useState((photo.subjects ? JSON.parse(photo.subjects) : []).join(', '))

  function set<K extends keyof VerifyPayload>(k: K, v: VerifyPayload[K]) {
    setForm((f) => {
      const next = { ...f, [k]: v }
      // Auto-snap era to the year on year edits — saves a click per verify.
      if (k === 'year' || k === 'year_min' || k === 'year_max') {
        next.era = eraForYear(next.year).key as Era
      }
      return next
    })
  }

  function setNote(check: CheckKey, url: string) {
    setForm((f) => ({ ...f, verification_notes: { ...f.verification_notes, [check]: url } }))
  }

  const checksOK = form.v1_provenance && form.v2_license && form.v3_year_primary &&
    form.v4_year_second_src && form.v5_location && form.v6_no_conflict
  const notesOK = (Object.keys(CHECK_LABELS) as CheckKey[])
    .filter((k) => form[k])
    .every((k) => (form.verification_notes[k] ?? '').length > 0)
  const canSave = checksOK && notesOK && form.place_name_en && form.caption_en

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const payload = {
        ...form,
        subjects: subjectsRaw.split(',').map((s: string) => s.trim()).filter(Boolean),
      }
      const updated = await curatorApi.updatePhoto(photo.id, payload)
      onSaved(updated)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function reject() {
    if (!confirm('Reject this photo? It will be marked rejected and the image deleted.')) return
    await curatorApi.rejectPhoto(photo.id)
    onCancel()
  }

  return (
    <form onSubmit={save} className="bg-white border border-stone-200 rounded-lg p-4 space-y-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h2 className="font-semibold">Verify <span className="font-mono text-xs text-stone-400">{photo.id}</span></h2>
          <a href={photo.source_url} target="_blank" rel="noreferrer"
             className="text-xs text-stone-500 underline">
            {photo.source_archive} · view source ↗
          </a>
        </div>
        <button type="button" onClick={onCancel} className="text-sm text-stone-500 hover:text-stone-700">
          close
        </button>
      </header>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <img src={curatorApi.imageUrl(photo.id)} alt=""
               className="w-full rounded border border-stone-200" />
          <div className="text-xs text-stone-500 mt-2 space-y-1">
            <div>pHash: <span className="font-mono">{photo.phash.slice(0, 16)}…</span></div>
            <div>Size: {photo.width}×{photo.height}</div>
          </div>
        </div>

        <div className="space-y-4 text-sm">
          <Field label="License">
            <input value={form.license} onChange={(e) => set('license', e.target.value)}
                   className="w-full border border-stone-300 rounded px-2 py-1" />
          </Field>
          <Field label="Attribution text">
            <input value={form.attribution_text} onChange={(e) => set('attribution_text', e.target.value)}
                   className="w-full border border-stone-300 rounded px-2 py-1" />
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Year">
              <input type="number" value={form.year}
                     onChange={(e) => set('year', parseInt(e.target.value, 10))}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
            <Field label="Year min">
              <input type="number" value={form.year_min}
                     onChange={(e) => set('year_min', parseInt(e.target.value, 10))}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
            <Field label="Year max">
              <input type="number" value={form.year_max}
                     onChange={(e) => set('year_max', parseInt(e.target.value, 10))}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
          </div>

          <Field label="Era">
            <select value={form.era} onChange={(e) => set('era', e.target.value as Era)}
                    className="w-full border border-stone-300 rounded px-2 py-1">
              {ERAS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Lat">
              <input type="number" step="any" value={form.lat}
                     onChange={(e) => set('lat', parseFloat(e.target.value))}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
            <Field label="Lng">
              <input type="number" step="any" value={form.lng}
                     onChange={(e) => set('lng', parseFloat(e.target.value))}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
          </div>

          <Field label="Geo granularity">
            <select value={form.geo_granularity}
                    onChange={(e) => set('geo_granularity', e.target.value as GeoGranularity)}
                    className="w-full border border-stone-300 rounded px-2 py-1">
              {GRANULARITIES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Place (EN)">
              <input value={form.place_name_en}
                     onChange={(e) => set('place_name_en', e.target.value)}
                     className="w-full border border-stone-300 rounded px-2 py-1" />
            </Field>
            <Field label={<>Place (AR) <TranslateButton from={form.place_name_en} onResult={(t) => set('place_name_ar', t)} /></>}>
              <input value={form.place_name_ar}
                     onChange={(e) => set('place_name_ar', e.target.value)}
                     className="w-full border border-stone-300 rounded px-2 py-1" dir="rtl" />
            </Field>
          </div>

          <Field label="Place (historical, optional)">
            <input value={form.place_name_historical ?? ''}
                   onChange={(e) => set('place_name_historical', e.target.value || undefined)}
                   placeholder="e.g. Sharia Soliman Pasha"
                   className="w-full border border-stone-300 rounded px-2 py-1" />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Category">
              <select value={form.category} onChange={(e) => set('category', e.target.value as Category)}
                      className="w-full border border-stone-300 rounded px-2 py-1">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Event anchor">
              <select value={form.event_anchor ?? ''}
                      onChange={(e) => set('event_anchor', e.target.value || undefined)}
                      className="w-full border border-stone-300 rounded px-2 py-1">
                {EVENT_ANCHORS.map((a) => <option key={a} value={a}>{a || '— none —'}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Subjects (comma-separated, e.g. nasser, umm_kulthum)">
            <input value={subjectsRaw} onChange={(e) => setSubjectsRaw(e.target.value)}
                   className="w-full border border-stone-300 rounded px-2 py-1" />
          </Field>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <Field label="Caption (EN)">
          <textarea value={form.caption_en} onChange={(e) => set('caption_en', e.target.value)}
                    rows={2}
                    className="w-full border border-stone-300 rounded px-2 py-1" />
        </Field>
        <Field label={<>Caption (AR) <TranslateButton from={form.caption_en} onResult={(t) => set('caption_ar', t)} /></>}>
          <textarea value={form.caption_ar} onChange={(e) => set('caption_ar', e.target.value)}
                    rows={2} dir="rtl"
                    className="w-full border border-stone-300 rounded px-2 py-1" />
        </Field>
      </div>

      <div className="space-y-2 text-sm">
        <h3 className="font-semibold">Verification (6 checks)</h3>
        {(Object.keys(CHECK_LABELS) as CheckKey[]).map((key) => (
          <div key={key} className="flex gap-2 items-start">
            <input type="checkbox" checked={form[key]}
                   onChange={(e) => set(key, e.target.checked)}
                   className="mt-1" />
            <div className="flex-1">
              <div className="text-stone-700">{CHECK_LABELS[key]}</div>
              {form[key] && (
                <input
                  type="url"
                  required
                  placeholder="Source URL (required when ticked)"
                  value={form.verification_notes[key] ?? ''}
                  onChange={(e) => setNote(key, e.target.value)}
                  className="w-full mt-1 border border-stone-300 rounded px-2 py-1 text-xs"
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <label className="text-sm">
          Nostalgia score: <strong>{form.nostalgia_score}</strong>/10
          <input type="range" min="1" max="10" value={form.nostalgia_score}
                 onChange={(e) => set('nostalgia_score', parseInt(e.target.value, 10))}
                 className="ml-2 align-middle" />
        </label>
        <label className="text-sm">
          Curator:
          <input value={form.curator} onChange={(e) => set('curator', e.target.value)}
                 className="ml-2 border border-stone-300 rounded px-2 py-1 text-sm" />
        </label>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex gap-2 justify-between pt-2 border-t border-stone-100">
        <button type="button" onClick={reject}
                className="text-sm text-red-600 hover:text-red-800">
          Reject photo
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel}
                  className="text-sm px-3 py-2 text-stone-600 hover:text-stone-900">
            Cancel
          </button>
          <button type="submit" disabled={!canSave || saving}
                  className="text-sm px-4 py-2 bg-stone-900 text-white rounded disabled:opacity-40">
            {saving ? 'Saving…' : checksOK ? 'Save & verify' : 'Save (still candidate)'}
          </button>
        </div>
      </div>
    </form>
  )
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-stone-500 mb-1">{label}</span>
      {children}
    </label>
  )
}

function TranslateButton({
  from, onResult,
}: { from: string; onResult: (text: string) => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function go(e: React.MouseEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const out = await curatorApi.translate(from)
      onResult(out)
    } catch (err) {
      setError((err as Error).message)
    } finally { setBusy(false) }
  }
  return (
    <span>
      <button type="button" onClick={go} disabled={busy || !from.trim()}
              className="text-[10px] text-blue-600 hover:text-blue-800 underline disabled:opacity-40 ms-2">
        {busy ? 'translating…' : 'translate ←'}
      </button>
      {error && <span className="text-[10px] text-red-600 ms-2">{error}</span>}
    </span>
  )
}
